import { memo, useState, useCallback, useEffect, SyntheticEvent, useMemo } from "react";
import {
  Modal,
  Form,
  FormField,
  FormGroup,
  Button,
  Input,
  Header,
  Dropdown,
  DropdownProps,
  Label,
} from "semantic-ui-react";

// Serializers
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";

// Types
import { BTN_ACTS, PickedState } from "./types/MeBillingType";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { useIntl } from "react-intl";

export type NewRegisterFormData = {
  division_name: string;
  provider_id: number;
};

type ModNewRegisterProps = {
  open: boolean;
  setProp: any;
  onClose: () => void;
  onSave: (data: NewRegisterFormData) => void;

  // options
  doctorList?: DoctorSerializer[];

  // common
  isUpdateEncounter?: boolean;
  selectedPatient?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  languageUX: "en" | "th";
} & Pick<PickedState, "buttonLoadCheck" | "selectedDivision">;

const ModNewRegister = (props: ModNewRegisterProps) => {
  const intl = useIntl();
  const { open, onClose, onSave, doctorList, buttonLoadCheck, languageUX } = props;

  const INITIAL_FORM: NewRegisterFormData = {
    division_name: props.selectedDivision?.name || "",
    provider_id: 0,
  };

  const [formData, setFormData] = useState<NewRegisterFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof NewRegisterFormData, string>>>({});

  const isUpdate = props.isUpdateEncounter;
  const action = isUpdate ? BTN_ACTS.UPDATE_VISIT : BTN_ACTS.CREATE_VISIT;

  const providerOptions = useMemo(() => {
    return (doctorList || []).map((item) => ({
      key: item.id,
      value: item.id,
      text:
        (item.full_name || "")?.trim().length > 0
          ? item.full_name
          : `${item.first_name} ${item.last_name}`,
    }));
  }, [doctorList]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        ...INITIAL_FORM,
        ...(props.isUpdateEncounter && { provider_id: props.selectedEncounter?.doctor }),
      });
      setErrors({});
    }
  }, [open, props.selectedEncounter, props.isUpdateEncounter]);

  // ================================
  // Handlers
  // ================================

  const handleChangeDropdown = useCallback(
    (e: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
      const { name, value } = data;

      setFormData((prev) => ({
        ...prev,
        [name as string]: value,
      }));

      if (errors[name as keyof NewRegisterFormData]) {
        setErrors((prev) => ({ ...prev, [name as string]: undefined }));
      }
    },
    [errors],
  );

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof NewRegisterFormData, string>> = {};

    if (!formData.provider_id) {
      newErrors.provider_id = "กรุณาเลือกผู้ให้บริการ";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = useCallback(() => {
    if (validate()) {
      onSave(formData);
    }
  }, [formData, onSave]);

  const handleClose = useCallback(() => {
    setFormData(INITIAL_FORM);
    setErrors({});
    onClose();
  }, [onClose]);

  // ================================
  // Render
  // ================================

  const requiredMark = <span style={{ color: "red" }}>*</span>;
  const labelStyle = { minWidth: "100px", fontWeight: 500 };

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Modal.Content>
        <Header
          as="h2"
          style={{
            color: "var(--primary-theme-color)",
            marginBottom: "1.5rem",
          }}
        >
          {props.isUpdateEncounter ? "แก้ไขรายละเอียดการเข้ารับบริการ" : "ลงทะเบียนรับบริการ"}
        </Header>

        <Form>
          {/* Patient Name (Read-only) */}
          {props.isUpdateEncounter && (
            <FormGroup inline>
              <FormField width={4}>
                <label style={labelStyle}>ชื่อผู้ใช้บริการ</label>
              </FormField>
              <FormField width={12}>
                <label style={labelStyle}>{props.selectedPatient?.full_name}</label>
              </FormField>
            </FormGroup>
          )}

          {/* Division Name (Read-only) */}
          <FormGroup inline>
            <FormField width={4}>
              <label style={labelStyle}>ชื่อบัญชีทางการ</label>
            </FormField>
            <FormField width={12}>
              {props.isUpdateEncounter ? (
                <label style={labelStyle}>{props.selectedPatient?.division_name}</label>
              ) : (
                <Input
                  fluid
                  readOnly
                  value={formData.division_name}
                  style={{ backgroundColor: "#f5f5f5" }}
                />
              )}
            </FormField>
          </FormGroup>

          {/* Package Name (Read-only) */}
          {props.isUpdateEncounter && props.selectedPatient?.package && (
            <FormGroup
              inline
              style={{
                display: "flex",
                alignItems: "baseline",
              }}
            >
              <FormField width={4}>
                <label style={labelStyle}>Package ที่รับบริการ</label>
              </FormField>
              <FormField width={12}>
                <div>
                  {props.selectedPatient?.package?.length > 0
                    ? (props.selectedPatient?.package || []).map((data: any) => {
                        return (
                          <div style={{ display: "flex" }}>
                            <label
                              style={{
                                ...labelStyle,
                                color: "#FE9500",
                                ...(data.package_type_name === "Package" && { color: "#21ba45" }),
                              }}
                            >
                              {data?.package_type_name || ""}
                            </label>
                            <label style={labelStyle}>{data?.product_name || ""}</label>
                          </div>
                        );
                      })
                    : "-"}
                </div>
              </FormField>
            </FormGroup>
          )}

          {/* Provider Dropdown */}
          <FormGroup inline>
            <FormField width={4}>
              <label style={labelStyle}>ผู้ให้บริการ{requiredMark}</label>
            </FormField>
            <FormField width={12} error={!!errors.provider_id}>
              <div style={{ width: "100%" }}>
                <Dropdown
                  fluid
                  selection
                  search
                  clearable
                  name="provider_id"
                  placeholder="เลือกผู้ให้บริการ"
                  options={providerOptions}
                  value={formData.provider_id}
                  onChange={handleChangeDropdown}
                  error={!!errors.provider_id}
                />
                {errors.provider_id && (
                  <span style={{ color: "red", fontSize: "12px" }}>{errors.provider_id}</span>
                )}
              </div>
            </FormField>
          </FormGroup>
        </Form>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "1rem",
            marginTop: "2rem",
          }}
        >
          <Button
            onClick={handleClose}
            style={{
              backgroundColor: "#4a4a4a",
              color: "white",
              minWidth: "120px",
            }}
          >
            ปิดหน้าต่าง
          </Button>
          <ButtonLoadCheck
            setProp={props.setProp}
            onClick={handleSubmit}
            paramKey={action}
            buttonLoadCheck={props.buttonLoadCheck?.[action]}
            color={isUpdate ? "yellow" : undefined}
            style={{
              minWidth: "120px",
              ...(isUpdate
                ? {}
                : {
                    backgroundColor: "var(--primary-theme-color)",
                    color: "white",
                  }),
            }}
            title={intl.formatMessage({
              id: isUpdate ? "บันทึกการแก้ไข" : "บันทึกข้อมูล",
            })}
          />
        </div>
      </Modal.Content>
    </Modal>
  );
};

ModNewRegister.displayName = "ModNewRegister";

export default memo(ModNewRegister);
